import { useEffect } from 'react';
import { Link } from 'react-router-dom';

type Status = 'Live' | 'Live with limits' | 'Needs set-up by Revvly' | 'Not available yet';

interface Row {
  name: string;
  status: Status;
  detail: React.ReactNode;
}

const GOOGLE: Row[] = [
  {
    name: 'Google Drive import',
    status: 'Live with limits',
    detail: (
      <>
        Uses read access (the <span className="api-inline">drive.readonly</span> scope). You pick
        folders to sync; Revvly reads PDF, JPEG and PNG files and extracts invoice data for review
        before anything is committed. The import itself writes nothing to Drive; writing is client
        folders, below, which ask for more access.
      </>
    ),
  },
  {
    name: 'Google Drive client folders',
    status: 'Needs set-up by Revvly',
    detail: (
      <>
        Switched on per workspace by Revvly. A Drive folder per client and prospect, under roots you
        choose: Revvly creates folders and writes invoices, credit notes, uploads and attachments
        into them, and brings back files your team adds. This needs write access (the{' '}
        <span className="api-inline">drive</span> scope), asked for by Enable client folders. Revvly
        only writes inside the chosen folders and never deletes, trashes or re-shares anything. See{' '}
        <Link to="/docs/guides/drive-folders">Client folders in Google Drive</Link>.
      </>
    ),
  },
  {
    name: 'Gmail (mailbox)',
    status: 'Live with limits',
    detail: (
      <>
        Each user connects their own mailbox. Revvly reads and sends mail and manages drafts (
        <span className="api-inline">gmail.modify</span>) and imports your signature (
        <span className="api-inline">gmail.settings.basic</span>). It sits behind the CRM module. A
        workspace can also connect one shared accounting mailbox for invoice intake. Gmail is the
        only mailbox provider; see Outlook below.
      </>
    ),
  },
  {
    name: 'Google Calendar',
    status: 'Not available yet',
    detail:
      'The consent screen can ask for calendar permission, but nothing in Revvly reads or writes your Google Calendar today. Meetings reach the CRM from Calendly bookings and from calendar invites attached to synced email.',
  },
];

const WORKSPACE: Row[] = [
  {
    name: 'Slack',
    status: 'Live with limits',
    detail:
      'Install the Revvly app into your workspace, map members to Revvly users, and use it for OKR check-ins by direct message, the /revvly command and the conversational bot. Slack can also be used to sign in to an invited account. The app is installed by link and is not listed in the Slack Marketplace.',
  },
  {
    name: 'ClickUp (OAuth connection)',
    status: 'Live with limits',
    detail:
      'One connection serves three things: work links on OKR Key Results (refreshed about every 30 minutes), ClickUp Chat as a check-in channel (replies are polled each minute and this path has had less production use), and Support work items. Support can link tasks, create a task from a ticket and add comments to linked tasks. Revvly only adds tasks and comments; it never moves or edits an existing task.',
  },
  {
    name: 'Calendly',
    status: 'Live',
    detail:
      'Bookings arrive as CRM meetings and, for new people, as prospects. You connect with a Calendly token under Settings, Booking intake. Revvly does not create bookings in Calendly.',
  },
  {
    name: 'Intercom and Zendesk help centres',
    status: 'Live with limits',
    detail:
      'Import a help centre into the Support knowledge base using your own API token. Reads only; nothing is written back. Intercom also supports importing past conversations. A Zendesk conversation import does not exist.',
  },
  {
    name: 'Forms embed',
    status: 'Live',
    detail: (
      <>
        Published forms are served as a hosted page and as an embeddable script. See the{' '}
        <Link to="/docs/guides/forms">Forms guide</Link>.
      </>
    ),
  },
];

const AUTOMATION: Row[] = [
  {
    name: 'Zapier and Make',
    status: 'Live with limits',
    detail:
      'There is no Zapier or Make app to install. You paste a catch-hook URL into an outbound webhook endpoint, signed with a per-endpoint secret. The only events sent today are CRM lead.created, lead.updated and lead.deleted, optionally filtered by pipeline. Support has its own, separate ticket webhooks.',
  },
  {
    name: 'MCP for Claude',
    status: 'Live',
    detail: (
      <>
        Connect Claude or any MCP client with OAuth or an API key. Reads are live; writes are
        proposals a signed-in person approves. See{' '}
        <Link to="/docs/api/mcp">Connect Claude with MCP</Link>.
      </>
    ),
  },
  {
    name: 'Public GraphQL API',
    status: 'Live',
    detail: (
      <>
        API-key access to clients, subscriptions, invoices and payment summaries, with a growing set
        of writes. See <Link to="/docs/api/graphql-public">Public GraphQL API</Link>.
      </>
    ),
  },
  {
    name: 'REST usage and events API',
    status: 'Live',
    detail: (
      <>
        Report metered usage and stream signed events. See{' '}
        <Link to="/docs/api/events">Event Ingestion API</Link> and the{' '}
        <Link to="/docs/guides/usage-reporting">usage reporting guide</Link>.
      </>
    ),
  },
  {
    name: 'Payment webhook (inbound)',
    status: 'Live',
    detail: (
      <>
        Your payment system posts invoice payment status into Revvly. See{' '}
        <Link to="/docs/api/invoice-payments">Invoice Payment Webhook</Link>.
      </>
    ),
  },
];

const PLATFORM: Row[] = [
  {
    name: 'Email sending',
    status: 'Live',
    detail:
      'Invoices, sign-in and invitation emails, and Support notifications are sent by Revvly through Amazon SES. Email you send to a client from the CRM goes out through your own connected Gmail mailbox.',
  },
  {
    name: 'Sign-in and SSO',
    status: 'Not available yet',
    detail:
      'Accounts sign in with email and password, or with Slack for people invited that way. There is no SAML, OIDC or enterprise single sign-on, and no Google sign-in.',
  },
  {
    name: 'Installable app and push',
    status: 'Needs set-up by Revvly',
    detail:
      'The dashboard installs as a PWA. Browser push notifications work only once Revvly has configured the push keys for the environment. Triggers: subscription renewed or churned, invoice paid or overdue, unverified metered usage, a subscription not live after signing, and a shared document opened (sender only).',
  },
];

const ACCOUNTING: Row[] = [
  {
    name: 'Stripe',
    status: 'Live with limits',
    detail:
      'An admin connects the workspace’s own Stripe account under Settings, Payments. A client’s card is saved through Stripe’s own form (or startPaymentMethodSetup on the public API), so card numbers never reach Revvly, and invoices issued after both the connection and the card exist are charged automatically. Invoices issued before, imports and backfilled history are never charged. A Stripe account can be connected to one workspace only. Card charges can be followed with the payment.* webhooks. Other payments are still recorded through the inbound payment webhook or by hand on the invoice.',
  },
  {
    name: 'QuickBooks and Xero',
    status: 'Not available yet',
    detail: 'Planned. Nothing is built, so no accounting push or sync exists today.',
  },
  {
    name: 'Outlook and Microsoft mail',
    status: 'Not available yet',
    detail:
      'Not supported. Mail sync, sending and drafts work with Gmail only. Meeting invites that arrive as calendar attachments are still read from Gmail.',
  },
];

const SUPPORT_HAS: Row[] = [
  {
    name: 'Tickets and inbox',
    status: 'Live',
    detail:
      'Tickets with public replies and internal notes, assignment, status workflows, attachments that are scanned before use, and notifications by email and in the app.',
  },
  {
    name: 'Knowledge base',
    status: 'Live',
    detail:
      'Sources are a website crawl, uploads, Intercom or Zendesk help centres, and your own system through the API. Articles can be reviewed by audience before they are used.',
  },
  {
    name: 'AI first line',
    status: 'Live with limits',
    detail:
      'Answers from your knowledge base, with a daily spend cap you control. A resolution is billed only when it is recorded as AI resolved. Needs the Support module and the AI provider key configured for the environment.',
  },
  {
    name: 'ClickUp work items',
    status: 'Live with limits',
    detail:
      'Link or create ClickUp tasks from a ticket, with client-facing stages. See the ClickUp row above for limits.',
  },
  {
    name: 'Ticket webhooks',
    status: 'Live',
    detail:
      'Send ticket.created, ticket.message, ticket.status_changed, ticket.updated, ticket.closed, ticket.reopened and ticket.progressed to your own endpoint.',
  },
  {
    name: 'Chat API for your own widget',
    status: 'Live',
    detail: (
      <>
        Your server mints an end-user token and your front end talks to the Support chat API. See{' '}
        <Link to="/docs/guides/support-widget">Build a Support widget</Link> and the{' '}
        <Link to="/docs/api/support-chat">Support chat API</Link>.
      </>
    ),
  },
];

const SUPPORT_NOT: Row[] = [
  {
    name: 'SLAs',
    status: 'Not available yet',
    detail: 'No response or resolution targets, timers or breach alerts.',
  },
  {
    name: 'Macros and canned replies',
    status: 'Not available yet',
    detail: 'No saved replies. Agents write each reply.',
  },
  {
    name: 'Automation triggers',
    status: 'Not available yet',
    detail:
      'No rules that act when a ticket changes. A status workflow only defines which statuses exist and which moves are allowed.',
  },
  {
    name: 'Routing and round-robin',
    status: 'Not available yet',
    detail:
      'Tickets are assigned by a person. The only routing setting picks a default ClickUp list for each ticket type.',
  },
  {
    name: 'An email channel',
    status: 'Not available yet',
    detail:
      'Revvly sends Support notification emails, but a customer cannot open or answer a ticket by emailing a support address.',
  },
  {
    name: 'A packaged chat widget',
    status: 'Not available yet',
    detail: 'There is no drop-in widget. You build the chat front end on the chat API.',
  },
];

function Table({ rows }: { rows: Row[] }) {
  return (
    <div className="dp-fields-table">
      {rows.map((r) => (
        <div className="dp-field-row" key={r.name}>
          <div className="dp-field-k">
            {r.name}
            <br />
            <span className="api-inline">{r.status}</span>
          </div>
          <div className="dp-field-v">{r.detail}</div>
        </div>
      ))}
    </div>
  );
}

export function IntegrationsAvailability() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Integrations and availability — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/reference">Reference</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Integrations and availability</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Reference</span>
        <h1>
          Integrations and <em>availability</em>
        </h1>
        <p className="dp-oneliner">
          What connects to Revvly today, what has limits, and what does not exist yet. Checked
          against the product in October 2026.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">01</span> How to read the status
        </h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Live</div>
            <div className="dp-field-v">You can use it now with no help from Revvly.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Live with limits</div>
            <div className="dp-field-v">It works, with the boundaries named in the row.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Needs set-up by Revvly</div>
            <div className="dp-field-v">
              It depends on a key or setting only Revvly can configure. Ask us to switch it on.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Not available yet</div>
            <div className="dp-field-v">
              It does not exist in the product. Work in progress or on a plan is not counted.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">02</span> Google
        </h2>
        <p className="dp-plain">
          One Google consent screen covers Drive, Gmail and Calendar. The permissions listed here
          are what each one is actually used for.
        </p>
        <Table rows={GOOGLE} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">03</span> Workspace tools
        </h2>
        <Table rows={WORKSPACE} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">04</span> APIs and automation
        </h2>
        <Table rows={AUTOMATION} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">05</span> Platform
        </h2>
        <Table rows={PLATFORM} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">06</span> Payments, accounting and other mail
        </h2>
        <Table rows={ACCOUNTING} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">07</span> Support: what exists
        </h2>
        <p className="dp-plain">
          Revvly Support needs the Support module turned on for your workspace. Setup steps are in
          the <Link to="/docs/guides/support-setup">Support setup guide</Link>.
        </p>
        <Table rows={SUPPORT_HAS} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">08</span> Support: what is not part of it today
        </h2>
        <Table rows={SUPPORT_NOT} />
      </section>
    </div>
  );
}
